import React, { useState, useEffect, useRef } from 'react';

// Parse SRT content into structured data
const parseSRT = (srtContent) => {
  if (!srtContent || typeof srtContent !== 'string') {
    return [];
  }

  const subtitles = [];
  const blocks = srtContent.trim().split('\n\n').filter(Boolean);
  
  blocks.forEach(block => {
    const lines = block.split('\n');
    if (lines.length >= 3) {
      // Parse timecode (00:00:00,000 --> 00:00:00,000)
      const times = lines[1].split(' --> ');
      if (times.length !== 2) {
        return;
      }
      const startTime = timeToSeconds(times[0]);
      const endTime = timeToSeconds(times[1]);
      
      // Combine remaining lines as text
      const text = lines.slice(2).join('\n');
      
      subtitles.push({
        index: parseInt(lines[0]),
        start: startTime,
        end: endTime,
        text
      });
    }
  });
  
  return subtitles;
};

const normalizeWordTimestamps = (rawWordTimestamps) => {
  if (!Array.isArray(rawWordTimestamps)) {
    return [];
  }

  const normalized = [];
  rawWordTimestamps.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const rawWord = String(item.word || item.text || '').trim();
    if (!rawWord) {
      return;
    }

    const startRaw = Number(item.start_seconds ?? item.start_time ?? item.start ?? 0);
    const endRaw = Number(item.end_seconds ?? item.end_time ?? item.end ?? (startRaw + 0.05));

    if (!Number.isFinite(startRaw)) {
      return;
    }

    const startSeconds = Math.max(0, startRaw);
    const endSeconds = Number.isFinite(endRaw) && endRaw > startSeconds
      ? endRaw
      : (startSeconds + 0.05);

    normalized.push({
      word: rawWord,
      start_seconds: startSeconds,
      end_seconds: endSeconds
    });
  });

  normalized.sort((a, b) => {
    if (a.start_seconds !== b.start_seconds) {
      return a.start_seconds - b.start_seconds;
    }
    return a.end_seconds - b.end_seconds;
  });

  return normalized;
};

const tokenRequiresLeadingSpace = (previousToken, token) => {
  if (!token) return false;
  if (!previousToken) return false;
  if (/^[,.;:!?)\]}'"’]/.test(token)) return false;
  if (/^['"’]/.test(token)) return false;
  if (/[([{'"“]$/.test(previousToken)) return false;
  return true;
};

// Convert SRT timecode to seconds
const timeToSeconds = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return 0;
  }

  const [time, ms] = timeString.split(',');
  if (!time) {
    return 0;
  }

  const [hours, minutes, seconds] = time.split(':');
  const h = Number(hours || 0);
  const m = Number(minutes || 0);
  const s = Number(seconds || 0);
  const milli = Number(ms || 0);
  
  return (
    h * 3600 +
    m * 60 +
    s +
    milli / 1000
  );
};

const SRTViewer = ({
  srtContent,
  currentTime,
  duration,
  isVisible,
  onToggle,
  wordTimestamps = [],
  playerClearanceOverride = 0
}) => {
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [normalizedWords, setNormalizedWords] = useState([]);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const activeWordRef = useRef(null);
  const lastActiveWordIndexRef = useRef(-1);
  const viewerRef = useRef(null);
  const usingWordTimeline = normalizedWords.length > 0;
  const playerClearance = Math.max(
    isMobile ? 210 : 340,
    Number(playerClearanceOverride) || 0
  );
  const transcriptTailPadding = playerClearance + (isMobile ? 96 : 140);
  const panelBottom = isMobile ? 24 : 36;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Parse SRT content on mount
  useEffect(() => {
    if (srtContent) {
      setSubtitles(parseSRT(srtContent));
      return;
    }
    setSubtitles([]);
  }, [srtContent]);

  useEffect(() => {
    const normalized = normalizeWordTimestamps(wordTimestamps);
    setNormalizedWords(normalized);
    setActiveWordIndex(-1);
    lastActiveWordIndexRef.current = -1;
  }, [wordTimestamps]);

  // Word-level active token tracking (incremental seek through timeline).
  useEffect(() => {
    if (!usingWordTimeline) {
      return;
    }
    if (!normalizedWords.length) {
      setActiveWordIndex(-1);
      lastActiveWordIndexRef.current = -1;
      return;
    }

    let idx = lastActiveWordIndexRef.current;
    if (!Number.isFinite(idx) || idx < 0 || idx >= normalizedWords.length) {
      idx = 0;
    }

    if (currentTime < normalizedWords[idx].start_seconds) {
      while (idx > 0 && currentTime < normalizedWords[idx].start_seconds) {
        idx -= 1;
      }
      if (currentTime < normalizedWords[idx].start_seconds) {
        idx = -1;
      }
    } else {
      while (
        idx + 1 < normalizedWords.length &&
        currentTime >= normalizedWords[idx + 1].start_seconds
      ) {
        idx += 1;
      }
      if (idx >= 0 && currentTime > normalizedWords[idx].end_seconds) {
        const nextIdx = idx + 1;
        if (
          nextIdx < normalizedWords.length &&
          currentTime >= normalizedWords[nextIdx].start_seconds
        ) {
          idx = nextIdx;
        }
      }
    }

    lastActiveWordIndexRef.current = idx;
    if (idx !== activeWordIndex) {
      setActiveWordIndex(idx);
    }
  }, [currentTime, normalizedWords, activeWordIndex, usingWordTimeline]);

  useEffect(() => {
    if (!usingWordTimeline || activeWordIndex < 0 || !activeWordRef.current) {
      return;
    }
    activeWordRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });
  }, [activeWordIndex, usingWordTimeline]);

  // Fallback SRT mode: follow the LINE, not the clock. The old version scrolled by currentTime / duration, which assumes speech is spread evenly:
  // a Story Time episode has a title song and an eyecatch with no lines, so the transcript ran ahead through every one of them (founder, 19 Sep 2026).
  // The active cue is the last one that has started; through music it stays on the line just spoken instead of jumping back to the first.
  useEffect(() => {
    if (usingWordTimeline || !subtitles.length) return;
    let active = subtitles[0];
    for (const sub of subtitles) { if (sub.start <= currentTime) active = sub; else break; }
    setCurrentSubtitle((prev) => (prev && prev.index === active.index ? prev : active));
  }, [currentTime, subtitles, usingWordTimeline]);

  useEffect(() => {
    if (usingWordTimeline || !currentSubtitle || !viewerRef.current) return;
    const el = viewerRef.current.querySelector(`#subtitle-${currentSubtitle.index}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, [currentSubtitle, usingWordTimeline]);

  if (!isVisible) return null;

  return (
    <>
      {/* Close control lives OUTSIDE the scrolling panel: the transcript
          auto-scrolls while audio plays, and taps on elements inside a
          smooth-scrolling container are unreliable on mobile. */}
      <button
        onClick={onToggle}
        aria-label="Close read-along"
        style={{
          position: 'fixed',
          top: 'calc(12px + env(safe-area-inset-top, 0px))',
          right: '16px',
          zIndex: 960,
          width: '42px',
          height: '42px',
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(44, 40, 34, 0.88)',
          color: '#f8f5e9',
          fontSize: '18px',
          lineHeight: 1,
          cursor: 'pointer',
          boxShadow: '0 3px 12px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ✕
      </button>
    <div
      ref={viewerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? '8px' : '50%',
        right: isMobile ? '8px' : 'auto',
        bottom: `${panelBottom}px`,
        transform: isMobile ? 'none' : 'translateX(-50%)',
        width: isMobile ? 'auto' : '800px',
        maxWidth: isMobile ? 'none' : 'calc(100% - 48px)',
        maxHeight: '100%',
        height: 'auto',
        backgroundColor: '#f8f5e9',
        color: '#2c2c2c',
        overflowY: 'auto',
        padding: isMobile ? '20px 15px' : '40px 60px',
        zIndex: 950,
        boxShadow: 'inset 0 0 30px rgba(0,0,0,0.1), inset 2px 0 5px rgba(0,0,0,0.05), inset -2px 0 5px rgba(0,0,0,0.05)',
        fontFamily: 'Georgia, "Times New Roman", serif',
        backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.02) 0%, transparent 10%, transparent 90%, rgba(0,0,0,0.02) 100%)',
        scrollBehavior: 'smooth',
        fontSize: isMobile ? '16px' : '18px'
      }}
    >
      <div style={{
        maxWidth: isMobile ? '100%' : '800px',
        margin: isMobile ? '20px auto' : '40px auto',
        lineHeight: '1.6',
        fontSize: isMobile ? '16px' : '18px',
        letterSpacing: '0.01em',
        padding: isMobile ? '0 10px' : 0,
        paddingBottom: `${transcriptTailPadding}px`
      }}>
        {usingWordTimeline ? (
          <div style={{
            padding: isMobile ? '8px 16px' : '12px 24px',
            margin: isMobile ? '0 0 16px 0' : '0 0 24px 0',
            borderLeft: '4px solid rgba(107, 95, 76, 0.2)',
            transition: 'background-color 0.2s ease',
            textAlign: 'justify',
            textJustify: 'inter-word',
            position: 'relative',
            borderRadius: '4px',
            cursor: 'default'
          }}>
            <div style={{
              marginTop: '5px',
              position: 'relative',
              textIndent: isMobile ? '1em' : '2em',
              fontSize: isMobile ? '1em' : '1.1em',
              fontWeight: 400,
              color: '#1a1a1a',
              textShadow: '0 0 1px rgba(0,0,0,0.1)',
              lineHeight: isMobile ? '1.75' : '1.95',
              fontFamily: 'Georgia, "Times New Roman", serif'
            }}>
              {normalizedWords.map((wordEntry, index) => {
                const token = wordEntry.word;
                const previousToken = index > 0 ? normalizedWords[index - 1].word : '';
                const needsSpace = tokenRequiresLeadingSpace(previousToken, token);
                const isActive = index === activeWordIndex;
                return (
                  <span key={`${index}-${wordEntry.start_seconds}-${token}`}>
                    {needsSpace ? ' ' : ''}
                    <span
                      ref={isActive ? activeWordRef : null}
                      style={{
                        textDecoration: isActive ? 'underline' : 'none',
                        textDecorationThickness: isActive ? '1.5px' : 'initial',
                        textDecorationColor: isActive ? 'rgba(32, 32, 32, 0.55)' : 'transparent',
                        textUnderlineOffset: isActive ? '2px' : 'initial',
                        transition: 'text-decoration-color 0.12s ease, color 0.12s ease',
                        color: isActive ? '#111111' : '#1a1a1a'
                      }}
                    >
                      {token}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        ) : subtitles.map((subtitle) => (
            <div
              key={subtitle.index}
              id={`subtitle-${subtitle.index}`}
              style={{
                padding: isMobile ? '8px 16px' : '12px 24px',
                margin: isMobile ? '0 0 16px 0' : '0 0 24px 0',
                backgroundColor: currentSubtitle?.index === subtitle.index
                  ? 'rgba(255, 243, 205, 0.8)'
                  : 'transparent',
                borderLeft: currentSubtitle?.index === subtitle.index
                  ? '4px solid #6b5f4c'
                  : '4px solid transparent',
                transition: 'background-color 0.3s ease',
                textAlign: 'justify',
                textJustify: 'inter-word',
                position: 'relative',
                borderRadius: '4px',
                boxShadow: currentSubtitle?.index === subtitle.index
                  ? '0 2px 8px rgba(0,0,0,0.05)'
                  : 'none',
                cursor: 'default'
              }}
            >
              <div style={{
                  marginTop: '5px',
                  position: 'relative',
                  textIndent: isMobile ? '1em' : '2em',
                  fontSize: isMobile ? '1em' : '1.1em',
                  fontWeight: 400,
                  color: '#1a1a1a',
                  textShadow: '0 0 1px rgba(0,0,0,0.1)',
                  wordSpacing: isMobile ? '0.03em' : '0.05em',
                  lineHeight: isMobile ? '1.6' : '1.8',
                  fontFamily: 'Georgia, "Times New Roman", serif'
                }}
              >
                {subtitle.text}
              </div>
            </div>
          ))
        }
      </div>
    </div>
    </>
  );
};

export default SRTViewer;
